/**
 * Discount Detector
 * Checks if automatic discounts apply to a product by simulating cart addition
 * and displays the discounted price
 */

class DiscountDetector {
  constructor() {
    if (window.discountDetectorInitialized) return;
    window.discountDetectorInitialized = true;
    
    this.checkedVariants = new Set();
    this.currentAbortController = null;
    this.pendingCheck = null;
    this.init();
    this.setupVariantChangeListener();
  }

  async init() {
    const indicators = document.querySelectorAll('[data-discount-check]');
    
    for (const indicator of indicators) {
      const variantId = indicator.dataset.variantId;
      if (variantId && variantId !== 'null') {
        await this.checkDiscount(variantId, indicator);
      }
    }
  }

  setupVariantChangeListener() {
    // Listen for Shopify's variant change events via PubSub
    if (typeof subscribe !== 'undefined') {
      subscribe('variant-change', async (event) => {
        const variantId = event.data?.variant?.id;
        if (variantId) {
          await this.handleVariantChangeById(variantId);
        }
      });

      subscribe('option-value-selection-change', async (event) => {
        // Wait a bit for the variant data to update
        setTimeout(async () => {
          const variantSelect = document.querySelector('variant-selects [data-selected-variant]');
          if (variantSelect) {
            try {
              const variantData = JSON.parse(variantSelect.innerHTML);
              if (variantData?.id) {
                await this.handleVariantChangeById(variantData.id);
              }
            } catch (e) {
              console.error('Error parsing variant data:', e);
            }
          }
        }, 100);
      });
    }

    // Fallback: Listen for form changes
    const productForm = document.querySelector('product-form, form[action*="/cart/add"]');
    if (productForm) {
      productForm.addEventListener('change', async (e) => {
        // Check if it's a variant selector
        if (e.target.name === 'id' || e.target.closest('[name="id"]')) {
          const newVariantId = e.target.value || e.target.closest('[name="id"]')?.value;
          if (newVariantId) {
            await this.handleVariantChangeById(newVariantId);
          }
        }
      });
    }
  }

  async handleVariantChangeById(variantId) {
    // Cancel any pending check
    if (this.pendingCheck) {
      clearTimeout(this.pendingCheck);
    }

    // Abort any ongoing request
    if (this.currentAbortController) {
      this.currentAbortController.abort();
    }

    // Reset the price display immediately
    const indicators = document.querySelectorAll('[data-discount-check]');
    for (const indicator of indicators) {
      this.resetPriceDisplay(indicator);
      indicator.dataset.variantId = variantId;
    }

    // Debounce: wait a bit before checking
    this.pendingCheck = setTimeout(async () => {
      // Create new abort controller for this check
      this.currentAbortController = new AbortController();
      const signal = this.currentAbortController.signal;

      try {
        for (const indicator of indicators) {
          // Check if this request was aborted
          if (signal.aborted) {
            return;
          }
          
          // Check discount for new variant
          await this.checkDiscount(variantId, indicator, signal);
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('Discount check cancelled');
        } else {
          console.error('Error checking discount:', error);
        }
      } finally {
        this.currentAbortController = null;
      }
    }, 300); // Wait 300ms before checking
  }

  resetPriceDisplay(indicatorElement) {
    // Hide the indicator
    indicatorElement.style.display = 'none';
    
    // Find the price item
    const priceItem = indicatorElement.closest('.price-item');
    if (!priceItem) return;

    // Check if it was modified (has strikethrough)
    const originalPriceElement = priceItem.querySelector('.price-item--original');
    if (originalPriceElement) {
      // Restore original price text
      const originalText = originalPriceElement.textContent;
      const visuallyHidden = priceItem.querySelector('.visually-hidden');
      
      priceItem.innerHTML = '';
      if (visuallyHidden) {
        priceItem.appendChild(visuallyHidden);
      }
      
      const textNode = document.createTextNode(originalText);
      priceItem.appendChild(textNode);
      priceItem.appendChild(indicatorElement);
    }
  }

  async checkDiscount(variantId, indicatorElement, signal = null) {
    try {
      // Check if aborted before starting
      if (signal?.aborted) {
        return;
      }

      // First, clean up any existing instances of this variant in cart
      await this.cleanupVariantFromCart(variantId, signal);
      
      // Check if aborted after cleanup
      if (signal?.aborted) {
        return;
      }

      // Get current cart state
      const currentCart = await this.getCart(signal);
      
      // Check if aborted
      if (signal?.aborted) {
        return;
      }

      // Add item to cart temporarily
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1
          }]
        }),
        signal: signal
      });

      if (!addResponse.ok) {
        // Item couldn't be added (maybe out of stock)
        return;
      }

      // Check if aborted
      if (signal?.aborted) {
        // Clean up the item we just added
        await this.cleanupVariantFromCart(variantId);
        return;
      }

      // Get cart with the item
      const cartWithItem = await this.getCart(signal);
      
      // Check if aborted
      if (signal?.aborted) {
        await this.cleanupVariantFromCart(variantId);
        return;
      }

      // Check if there are any discounts applied and get discount info
      const discountInfo = this.getDiscountInfo(cartWithItem, variantId);
      
      // Remove ALL instances of the item we added
      await this.cleanupVariantFromCart(variantId, signal);
      
      // Check if aborted before updating UI
      if (signal?.aborted) {
        return;
      }

      // Update price display if discount detected
      if (discountInfo.hasDiscount) {
        this.updatePriceDisplay(indicatorElement, discountInfo);
      }
      
    } catch (error) {
      if (error.name === 'AbortError') {
        // Silently handle abort
        console.log('Request aborted');
        return;
      }
      console.error('Error checking discount:', error);
    }
  }

  async getCart(signal = null) {
    const response = await fetch('/cart.js', { signal });
    return await response.json();
  }

  getDiscountInfo(cart, variantId) {
    const item = cart.items.find(i => i.variant_id.toString() === variantId.toString());
    
    if (!item) {
      return { hasDiscount: false };
    }

    let hasDiscount = false;
    let discountedPrice = item.final_line_price;
    let originalPrice = item.original_line_price;

    // Check for line item discounts
    if (item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0) {
      hasDiscount = true;
    }
    
    // Check if final_line_price is less than original_line_price
    if (item.original_line_price > item.final_line_price) {
      hasDiscount = true;
    }

    // Check for discounted_price
    if (item.price < item.original_price) {
      hasDiscount = true;
      discountedPrice = item.final_price;
      originalPrice = item.original_price;
    }

    return {
      hasDiscount,
      discountedPrice,
      originalPrice,
      item
    };
  }

  updatePriceDisplay(indicatorElement, discountInfo) {
    // Show the asterisk
    indicatorElement.style.display = 'inline';

    // Find the price container
    const priceItem = indicatorElement.closest('.price-item');
    if (!priceItem) return;

    // Find the price wrapper to check currency settings
    const priceWrapper = indicatorElement.closest('.price');
    const useCurrencyCode = priceWrapper?.dataset.currencyWithCode === 'true';

    // Get the current price text (without the asterisk)
    const currentPriceText = Array.from(priceItem.childNodes)
      .filter(node => node.nodeType === Node.TEXT_NODE)
      .map(node => node.textContent.trim())
      .join('');

    // Format the discounted price
    const discountedPriceFormatted = this.formatMoney(discountInfo.discountedPrice, useCurrencyCode);
    
    // Clear the price item content (except visually-hidden elements)
    const visuallyHidden = priceItem.querySelector('.visually-hidden');
    priceItem.innerHTML = '';
    
    // Re-add visually hidden if it existed
    if (visuallyHidden) {
      priceItem.appendChild(visuallyHidden);
    }

    // Add strikethrough original price
    const originalPriceSpan = document.createElement('s');
    originalPriceSpan.className = 'price-item--original';
    originalPriceSpan.style.color = 'rgba(var(--color-foreground), 0.75)';
    originalPriceSpan.style.fontSize = '0.9em';
    originalPriceSpan.style.marginRight = '0.5rem';
    originalPriceSpan.textContent = currentPriceText;
    priceItem.appendChild(originalPriceSpan);

    // Add discounted price
    const discountedPriceSpan = document.createElement('span');
    discountedPriceSpan.className = 'price-item--discounted';
    discountedPriceSpan.style.color = 'rgb(var(--color-foreground))';
    discountedPriceSpan.textContent = discountedPriceFormatted;
    priceItem.appendChild(discountedPriceSpan);

    // Add asterisk back
    priceItem.appendChild(indicatorElement);
    indicatorElement.style.display = 'inline';
  }

  formatMoney(cents, useCurrencyCode = false) {
    // Try to use Shopify's built-in formatMoney if available
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      const format = useCurrencyCode ? 
        (theme.moneyWithCurrencyFormat || Shopify.money_format) : 
        (theme.moneyFormat || Shopify.money_format);
      return Shopify.formatMoney(cents, format);
    }
    
    // Fallback: Get money format from theme
    let moneyFormat = useCurrencyCode ? 
      (theme?.moneyWithCurrencyFormat || '€{{amount}} EUR') : 
      (theme?.moneyFormat || '€{{amount}}');
    
    // Format the amount
    const amount = (cents / 100).toFixed(2);
    
    // Handle different money format patterns
    let formattedMoney = moneyFormat
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', Math.round(cents / 100))
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100));
    
    return formattedMoney;
  }

  async removeItem(cart, variantId) {
    // Find the item in cart
    const item = cart.items.find(i => i.variant_id.toString() === variantId.toString());
    if (item) {
      // Remove using updates endpoint
      const updates = {};
      updates[item.key] = 0;
      
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates })
      });
    }
  }

  async cleanupVariantFromCart(variantId, signal = null) {
    try {
      // Check if aborted
      if (signal?.aborted) {
        return;
      }

      const cart = await this.getCart(signal);
      
      // Check if aborted
      if (signal?.aborted) {
        return;
      }

      // Find all items with this variant ID
      const itemsToRemove = cart.items.filter(i => i.variant_id.toString() === variantId.toString());
      
      if (itemsToRemove.length > 0) {
        const updates = {};
        
        // Set all matching items to quantity 0
        itemsToRemove.forEach(item => {
          updates[item.key] = 0;
        });
        
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ updates }),
          signal: signal
        });
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        // Silently handle abort
        return;
      }
      console.error('Error cleaning up variant from cart:', error);
    }
  }
}

// Initialize when DOM and dependencies are ready
function initDiscountDetector() {
  // Wait for pubsub to be available (optional dependency)
  const checkPubSub = setInterval(() => {
    if (typeof subscribe !== 'undefined' || document.readyState === 'complete') {
      clearInterval(checkPubSub);
      new DiscountDetector();
    }
  }, 50);
  
  // Fallback: initialize after 2 seconds even if pubsub isn't available
  setTimeout(() => {
    clearInterval(checkPubSub);
    if (!window.discountDetectorInitialized) {
      new DiscountDetector();
    }
  }, 2000);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDiscountDetector);
} else {
  initDiscountDetector();
}
